import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { InMemoryCache, ApolloLink, ApolloClientOptions } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { MarkdownModule } from 'ngx-markdown';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';
import { POSSIBLE_TYPES, TYPE_POLICIES } from './apollo-type-policies';
import { createGraphqlAuthRefreshLink } from './apollo-auth-refresh.link';
import { AuthService } from './services/auth.service';
import { LanguageService } from './services/language.service';
import { routes } from './app.routes';
import { AuthInterceptor } from './services/auth-interceptor';

if (!environment.production) {
  loadDevMessages();
  loadErrorMessages();
}

/** Shared app providers for browser and SSR. */
export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
    }),
    LanguageService,
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    JwtHelperService,
    {
      provide: APOLLO_OPTIONS,
      useFactory: (httpLink: HttpLink, authService: AuthService): ApolloClientOptions => ({
        link: ApolloLink.from([
          createGraphqlAuthRefreshLink(authService),
          httpLink.create({ uri: environment.gqlUrl }),
        ]),
        cache: new InMemoryCache({
          possibleTypes: POSSIBLE_TYPES,
          typePolicies: TYPE_POLICIES,
        }),
      }),
      deps: [HttpLink, AuthService],
    },
    Apollo,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    importProvidersFrom(MarkdownModule.forRoot()),
  ],
};
